import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { UEatProvider } from "@/contexts/UEatContext";
import { OrderingProvider } from "@/contexts/OrderingContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import MonthlySpecialPopup from "@/components/MonthlySpecialPopup";
import ComingSoonPage from "@/pages/ComingSoonPage";
import HomePage from "@/pages/HomePage";
import MenuPage from "@/pages/MenuPage";
import GalleryPage from "@/pages/GalleryPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CartPage from "@/pages/CartPage";
import CheckoutPage from "@/pages/CheckoutPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import HolidayMenuPage from "@/pages/HolidayMenuPage";
import JobsPage from "@/pages/JobsPage";
import RetailersPage from "@/pages/RetailersPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TraiteurPage from "@/pages/TraiteurPage";
import CamionPage from "@/pages/CamionPage";
import CookieConsent from "@/components/CookieConsent";
import { Toaster } from "@/components/ui/sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function MenuOnlyView({ onBack }) {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f5f5f0' }} data-testid="menu-only-view">
      <div className="sticky top-0 z-50 bg-black text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <button
          onClick={onBack}
          className="text-sm font-semibold hover:text-[#f4d00d] transition-colors flex items-center gap-2"
          data-testid="back-to-coming-soon"
        >
          ← Retour / Back
        </button>
        <span className="text-xs text-gray-300 tracking-wider uppercase font-bold">Belgo — Menu Preview</span>
        <span className="w-12" />
      </div>
      <main className="flex-1">
        <MenuPage />
      </main>
    </div>
  );
}

function AppContent({ siteLive, onAccessGranted }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith('/admin');

  // Apply theme from API to CSS variables and load Google Font
  useEffect(() => {
    axios.get(`${API_URL}/api/site-content/theme`).then(r => {
      const t = r.data || {};
      const root = document.documentElement;
      if (t.color_primary) root.style.setProperty('--belgo-primary', t.color_primary);
      if (t.color_accent) root.style.setProperty('--belgo-accent', t.color_accent);
      if (t.color_text) root.style.setProperty('--belgo-text', t.color_text);
      if (t.color_bg) root.style.setProperty('--belgo-bg', t.color_bg);
      if (t.font_family && t.font_family !== 'Inter') {
        const id = 'belgo-google-font';
        let link = document.getElementById(id);
        if (!link) {
          link = document.createElement('link');
          link.id = id;
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
        link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(t.font_family)}:wght@400;500;600;700;800&display=swap`;
        document.body.style.fontFamily = `"${t.font_family}", system-ui, sans-serif`;
      }
    }).catch(() => {});
  }, []);

  // If site is not live and user is not on admin pages and no access granted
  if (!siteLive && !isAdmin) {
    // Special case: allow standalone menu preview from Coming Soon page
    if (location.pathname === '/menu') {
      return <MenuOnlyView onBack={() => navigate('/')} />;
    }
    return <ComingSoonPage onAccessGranted={onAccessGranted} />;
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#f5f5f0' }}>
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/menu" element={<MenuPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/holiday-menu" element={<HolidayMenuPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/retailers" element={<RetailersPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/traiteur" element={<TraiteurPage />} />
          <Route path="/camion" element={<CamionPage />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={<AdminDashboard />} />
        </Routes>
      </main>
      <Footer />
      {siteLive && !isAdmin && <MonthlySpecialPopup />}
      <CookieConsent />
    </div>
  );
}

function App() {
  const [siteLive, setSiteLive] = useState(false);

  useEffect(() => {
    const checkLaunch = async () => {
      try {
        const { data: d } = await axios.get(`${API_URL}/api/settings/launch`);
        if (d.site_live) {
          setSiteLive(true);
        } else {
          const launchDate = new Date(d.launch_date);
          if (new Date() >= launchDate) {
            setSiteLive(true);
          } else {
            const accessGranted = sessionStorage.getItem('belgo_access_granted');
            setSiteLive(accessGranted === '1');
          }
        }
      } catch {
        setSiteLive(false);
      }
    };
    checkLaunch();
  }, []);

  const handleAccessGranted = () => setSiteLive(true);

  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <UEatProvider>
            <OrderingProvider>
              <BrowserRouter>
                <AppContent siteLive={siteLive} onAccessGranted={handleAccessGranted} />
                <Toaster position="top-right" />
              </BrowserRouter>
            </OrderingProvider>
          </UEatProvider>
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
